import AddIcon from "@mui/icons-material/Add";
import CachedIcon from "@mui/icons-material/Cached";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  Modal,
  Paper,
  Typography,
} from "@mui/material";
import { addDays } from "date-fns";
import {
  MRT_ColumnDef,
  MRT_Row,
  MRT_TableInstance,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import "react-multi-date-picker/styles/colors/green.css";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import getAxiosError from "../../app/api/axiosError";
import {
  downloadClientDataToCsv,
  downloadClientDataToXlsx,
  getClientsList,
} from "../../app/features/client/clientSlice";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { Client } from "../../app/models/responseModels/clientsResponse";
import GridDataFilter, {
  FilterableColumns,
  FilterableColumnsState,
} from "../../components/common/filter/GridDataFilter";
import {
  useSubmitModal,
  useSubmitModalWithParameter,
} from "../../components/common/modalPopup/useSubmitModal";
import MuiTabs, { TabsDetails } from "../../components/common/tabs/MuiTabs";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import { PAGES } from "../../enums/PagesEnum";
import { downloadCsv, downloadXlsx } from "../../helpers/downloadFile";
import ClientStore from "../dedicatedDelivery/actions/store/ClientStore";
import { getDefaultMRTOptions } from "./../dedicatedDelivery/DefaultMrtOptions";
import AssociatedParcelCenterGrid from "./associated parcelcenters/AssociatedParcelCenterGrid";
import {
  CLIENTS_COLUMN_NAME,
  CLIENTS_LABEL_NAME,
  DeliveryType,
  ddClientpageGridFilter,
  getDeliveryDescription,
} from "./ClientTypes";
import AddClientModal from "./forms/AddClientModal";
import EditClientModal from "./forms/EditClientModal";
import RateCardList from "./ratecard/RateCardList";
import ClientInvoiceList from "./invoices/ClientInvoiceList";
import { formatDateString, formatISODateString } from "../../components/common/utils/DateUtils";

const breadcrumb = ["ClientsDataGridView"];

const deliveryTypeShortFormMapping: Record<DeliveryType, string> = {
  DEDICATED: "dedicated",
  E2E: "e2e",
  BOTH: "both",
};

const deliveryTypeMapping: Record<DeliveryType, string> = {
  DEDICATED: "Dedicated Delivery",
  E2E: "Co-mingled Delivery",
  BOTH: "Both Way of Delivery",
};
const ClientsDatagridView = () => {
  const defaultMRTOptions = getDefaultMRTOptions<Client>();

  const dispatch = useAppDispatch();

  const { isClientLoading, clientList } = useSelector(
    (state: RootState) => state.client
  );

  const [clonedClientList, setClonedClientList] =
    useState<Client[]>(clientList);

  // keep startDate in state
  const [dateRange, setDateRange] = useState<DateObject[]>([
    new DateObject().setDate(addDays(new Date(), -30)),
    new DateObject().setDate(new Date()),
  ]);

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.CLIENTS
    )
  );

  const IsCreatePermission = pagePermission
    ? pagePermission.isCreatePermission
    : false;

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        )
      : false
  );

  // keep flag to enabled page loader
  const [dataFetchig, setDataFetchig] = useState<boolean>(true);

  // ***** States *****
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // flag to open/close route per store modal
  const [openActionsModal, setOpenActionsModal] = useState<boolean>(false);

  const [resetFilter, setResetFilter] = useState<boolean>(false);

  // ***** Callbacks *****
  const getClientsData = useCallback(async () => {
    try {
      const response = await dispatch(
        getClientsList({
          startDate: dateRange[0].format("YYYY-MM-DD"),
          endDate: dateRange[1].format("YYYY-MM-DD"),
          fdpId: "",
        })
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "clientList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "clientListException",
      });
    } finally {
      setDataFetchig(false);
      setResetFilter(false);
    }
  }, [dispatch, dateRange]);

  // ***** UseEffects *****
  useEffect(() => {
    if (dataFetchig) {
      getClientsData();
    }
  }, [dataFetchig, getClientsData]);

  useEffect(() => {
    setClonedClientList(clientList);
  }, [clientList]);

  // ***** UseMemos *****
  // Columns
  const columns = useMemo<MRT_ColumnDef<Client>[]>(
    () => [
      {
        header: CLIENTS_LABEL_NAME.CLIENT_ID,
        accessorKey: CLIENTS_COLUMN_NAME.CLIENT_ID,
        // filterVariant: "autocomplete",
      },
      {
        header: CLIENTS_LABEL_NAME.CLIENT_TYPE,
        Cell: ({ cell, row }) => {
          if (row.original.clientConfig && row.original.clientConfig.length) {
            return getDeliveryDescription(
              row.original.clientConfig,
              deliveryTypeMapping
            );
          } else {
            return "Co-mingled Delivery";
          }
        },
      },
      {
        accessorKey: CLIENTS_COLUMN_NAME.CLIENT_NAME,
        header: CLIENTS_LABEL_NAME.CLIENT_NAME,
      },
      {
        accessorKey: CLIENTS_COLUMN_NAME.DESCRIPTION,
        header: CLIENTS_LABEL_NAME.DESCRIPTION,
        whiteSpace: "normal",
        wordBreak: "break-word",
        size: 350,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.description && row.original.description.length > 0 ? (
              <span style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                {row.original.description}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        accessorKey: CLIENTS_COLUMN_NAME.BILLING_ADDRESS,
        header: CLIENTS_LABEL_NAME.BILLING_ADDRESS,
      },
      {
        accessorKey: CLIENTS_COLUMN_NAME.CITY,
        header: CLIENTS_LABEL_NAME.CITY,
      },
      {
        accessorKey: CLIENTS_COLUMN_NAME.STATE,
        header: CLIENTS_LABEL_NAME.STATE,
      },
      {
        accessorKey: CLIENTS_COLUMN_NAME.ZIP_CODE,
        header: CLIENTS_LABEL_NAME.ZIP_CODE,
      },
      {
        accessorKey: CLIENTS_COLUMN_NAME.TOTAL_STORES,
        header: CLIENTS_LABEL_NAME.TOTAL_STORES,
      },
      {
        accessorKey: CLIENTS_COLUMN_NAME.TOTAL_ROUTES,
        header: CLIENTS_LABEL_NAME.TOTAL_ROUTES,
      },
      {
        accessorKey: CLIENTS_COLUMN_NAME.PACKAGES_TENDERED,
        header: CLIENTS_LABEL_NAME.PACKAGES_TENDERED,
      },
      {
        accessorKey: CLIENTS_COLUMN_NAME.ORDERS_DELIVERED,
        header: CLIENTS_LABEL_NAME.ORDERS_DELIVERED,
      },
      {
        accessorKey: CLIENTS_COLUMN_NAME.UNDELIVERED_NO_ATTEMPT,
        header: CLIENTS_LABEL_NAME.UNDELIVERED_NO_ATTEMPT,
      },
      {
        accessorKey: CLIENTS_COLUMN_NAME.UNDELIVERED_ATTEMPT,
        header: CLIENTS_LABEL_NAME.UNDELIVERED_ATTEMPT,
      },
      {
        accessorKey: CLIENTS_COLUMN_NAME.GROSS_PERFORMANCE,
        header: CLIENTS_LABEL_NAME.GROSS_PERFORMANCE,
      },
      {
        accessorKey: CLIENTS_COLUMN_NAME.NET_PERFORMANCE,
        header: CLIENTS_LABEL_NAME.NET_PERFORMANCE,
      },
      {
        // filterVariant: "date",
        accessorKey: CLIENTS_COLUMN_NAME.CREATED_ON,
        header: CLIENTS_LABEL_NAME.CREATED_ON,
        Cell: ({ cell }) => {
                  const value = cell.getValue<string>();
                  if (!value) return "N/A";
                  const formattedDate = formatISODateString(value);
                  return formattedDate;
                },
      },
      {
        accessorKey: CLIENTS_COLUMN_NAME.UPDATED_ON,
        header: CLIENTS_LABEL_NAME.UPDATED_ON,
        Cell: ({ cell }) => {
                  const value = cell.getValue<string>();
                  if (!value) return "N/A";
                  const formattedDate = formatISODateString(value);
                  return formattedDate;
                },
      },
    ],
    []
  );

  // ***** Handlers *****
  const handleRefresh = () => {
    getClientsData();
  };

  const handleDownloadMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCsvFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadClientDataToCsv(clonedClientList)
      ).unwrap();

      if (response.isSuccess) {
        downloadCsv(response.data);
      } else {
        toast.error(response.message!, {
          toastId: "clientDownloadCsv",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "clientCsvException",
      });
    }
  };

  const handleXlsxFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadClientDataToXlsx(clonedClientList)
      ).unwrap();

      if (response) {
        downloadXlsx(response, "Client.xlsx");
      } else {
        toast.error("unable to download file", {
          toastId: "clientDownloadXlsx",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "clientDownloadXlsxException",
      });
    }
  };

  const [addClientModal, openAddClientModal] = useSubmitModal(
    (closeAddClientModal) => (
      <AddClientModal
        closeAddClientModal={closeAddClientModal}
        onCompleted={getClientsData}
      />
    )
  );

  const generateTabs = (
    clientType: string,
    clientData: MRT_Row<Client>,
    onComplete: Function
  ): TabsDetails[] => {
    if (clientType === "dedicated") {
      return [
        {
          tabName: "General",
          tabContent: (
            <EditClientModal
              clientData={clientData}
              onCompleted={onComplete}
              clientType={clientType}
              getAllClientData={getClientsData}
            />
          ),
          index: 0,
        },
        {
          tabName: "Store(s)",
          tabContent: (
            <ClientStore
              clientId={clientData.original.id}
              clientName={clientData.original.clientName}
              startDate={dateRange[0].toDate()}
              endDate={dateRange[1].toDate()}
            />
          ),
          index: 1,
        },
        {
          tabName: "Rate Card(s)",
          tabContent: (
            <RateCardList
              clientId={clientData.original.id}
              clientName={clientData.original.clientName}
              clientType={clientType}
              startDate={dateRange[0].toDate()}
              endDate={dateRange[1].toDate()}
            />
          ),
          index: 2,
        },
        {
          tabName: "Invoice (s)",
          tabContent: (
            <ClientInvoiceList
              clientId={clientData.original.id}
              clientName={clientData.original.clientName}
              clientType={clientType}
              startDate={dateRange[0].toDate()}
              endDate={dateRange[1].toDate()}
            />
          ),
          index: 3,
        },
      ];
    } else if (clientType === "both") {
      return [
        {
          tabName: "General",
          tabContent: (
            <EditClientModal
              clientData={clientData}
              onCompleted={onComplete}
              clientType={clientType}
              getAllClientData= {getClientsData}
            />
          ),
          index: 0,
        },
        {
          tabName: "Store(s)",
          tabContent: (
            <ClientStore
              clientId={clientData.original.id}
              clientName={clientData.original.clientName}
              startDate={dateRange[0].toDate()}
              endDate={dateRange[1].toDate()}
            />
          ),
          index: 1,
        },
        {
          tabName: "Parcel Center(s)",
          tabContent: (
            <AssociatedParcelCenterGrid
              clientId={clientData.original.id}
              data={clientData.original.clientParcelCenterFdps}
            />
          ),
          index: 2,
        },
        // {
        //   tabName: "Rate Card(s)",
        //   tabContent: (
        //     <Typography variant="h4" component="h1">
        //       hi This is tab 3
        //     </Typography>
        //   ),
        //   index: 3,
        // },
      ];
    } else {
      return [
        {
          tabName: "General",
          tabContent: (
            <EditClientModal
              clientData={clientData}
              onCompleted={onComplete}
              clientType={clientType}
              getAllClientData={getClientsData}
            />
          ),
          index: 0,
        },
        {
          tabName: "Parcel Center(s)",
          tabContent: (
            <AssociatedParcelCenterGrid
              clientId={clientData.original.id}
              data={clientData.original.clientParcelCenterFdps}
            />
          ),
          index: 1,
        },
      ];
    }
  };

  // open  modal logic  incase user selects any of the store
  const [actionsModal, doOpenActionsModal] = useSubmitModalWithParameter<
    MRT_Row<Client>
  >((rowData, closeActionsModal) => (
    <Modal
      aria-labelledby="new-store-add-modal"
      aria-describedby="new-store-add-modal-description"
      open={openActionsModal}
      onClose={() => {
        setOpenActionsModal(false);
      }}
      style={{ overflow: "scroll" }}
    >
      <Container component={Paper} style={{ marginTop: "2%", height: "auto" }}>
        <Grid
          container
          item
          alignItems="center"
          justifyContent="space-between"
          paddingTop={2}
        >
          <Typography variant="h4" component="h1">
            <i>{rowData.original.clientName}</i>
          </Typography>
          <IconButton
            onClick={() => {
              setOpenActionsModal(false);
            }}
          >
            <CloseRoundedIcon sx={{ color: "primary.secondary" }} />
          </IconButton>
        </Grid>
        <MuiTabs
          tabDetails={generateTabs(
            getDeliveryDescription(
              rowData.original.clientConfig,
              deliveryTypeShortFormMapping
            ).toLocaleLowerCase(),
            rowData,
            getClientsData
          )}
        />
      </Container>
    </Modal>
  ));

  // ***** Datagrid Custom Actions *****
  const renderTopToolbarCustomActions = (table: MRT_TableInstance<Client>) => {
    return (
      <Box display="flex" alignContent="center">
        <CustomTooltip arrow title="Refresh">
          <IconButton onClick={handleRefresh}>
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Export">
          <IconButton type="button" onClick={handleDownloadMenuClick}>
            <FileDownloadOutlinedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        {IsCreatePermission && IsAdmin && (
          <CustomTooltip
            arrow
            title="Add New"
            onClick={() => {
              openAddClientModal();
            }}
          >
            <IconButton type="button">
              <AddIcon sx={{ color: "primary.main" }} />
            </IconButton>
          </CustomTooltip>
        )}
      </Box>
    );
  };

  const table = useMaterialReactTable({
    ...defaultMRTOptions,
    columns: columns,
    data: clonedClientList,
    enableColumnFilters: true,
    initialState: {
      ...defaultMRTOptions.initialState,
      columnVisibility: {
        id: true,
        description: false,
        billingAddress: false,
        city: false,
        state: false,
        zipCode: false,
        totalRoutes: false,
      },
    },
    state: {
      showProgressBars: isClientLoading,
      sorting: [{ desc: true, id: "id" }],
    },
    muiPaginationProps: {
      color: "primary",
      rowsPerPageOptions: [50],
      variant: "outlined",
      showRowsPerPage: false,
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        doOpenActionsModal(row);
        setOpenActionsModal(true);
      },
      ...defaultMRTOptions.muiTableBodyRowProps,
      sx: {
        cursor: "pointer",
      },
    }),
    renderTopToolbarCustomActions: ({ table }) =>
      renderTopToolbarCustomActions(table),
    renderBottomToolbarCustomActions: () => {
      return (
        <Typography variant="subtitle2" component="p">
          Total Rows: {clonedClientList.length}
        </Typography>
      );
    },
  });

  return (
    <Box sx={{ width: "100%" }}>
      <>
        <DatePicker
          format="MM/DD/YYYY"
          className="green"
          dateSeparator=" ~ "
          range
          rangeHover
          maxDate={new Date()}
          value={dateRange}
          onChange={(dateObjects: DateObject[]) => {
            setDateRange(dateObjects);
          }}
        />
        <Button
          size="large"
          onClick={() => {
            setDataFetchig(true);
            setResetFilter(true);
          }}
        >
          SUBMIT
        </Button>
        <GridDataFilter
          filterableColumns={ddClientpageGridFilter}
          title="Search Clients"
          resetFlag={resetFilter}
          asyncSubmitFunction={(filterState: FilterableColumnsState) => {
            if (Object.keys(filterState).length) {
              const filteredData = clientList.filter(
                (client: Record<string, any>) => {
                  for (const key in filterState) {
                    if (filterState.hasOwnProperty(key)) {
                      const filterValues = filterState[key].values;
                      if (key === "createdOn" || key === "updatedOn") {
                        if (filterValues.length === 2) {
                          const clientCreatedDate = new Date(client[key]);
                          const startDate = new Date(filterValues[0]);
                          const endDate = new Date(filterValues[1]);

                          endDate.setHours(23, 59, 59, 999);

                          const isDateInRange =
                            clientCreatedDate >= startDate &&
                            clientCreatedDate <= endDate;
                          if (!isDateInRange) {
                            return false;
                          }
                        }
                      } else {
                        if (
                          filterValues.length > 0 &&
                          !filterValues.includes(client[key])
                        ) {
                          return false;
                        }
                      }
                    }
                  }
                  return true;
                }
              );

              console.log(filterState, clientList, filteredData);
              setClonedClientList(filteredData);
            } else {
              setClonedClientList(clientList);
            }
          }}
          asyncSelectedColumnValues={(
            columnName: string,
            setClFilterableColumns: React.Dispatch<
              React.SetStateAction<FilterableColumns>
            >
          ) => {
            const filteredData = clientList
              .map((e: Record<string, any>) => e[columnName])
              .filter((value) => value !== undefined && value !== null);
            setClFilterableColumns((prevState: FilterableColumns) => {
              return {
                ...prevState,
                [columnName]: {
                  ...prevState[columnName],
                  options: Array.from(new Set(filteredData)) ?? [],
                },
              };
            });
          }}
        />
        <Divider />
        <br />
      </>
      <MaterialReactTable table={table} />
      {actionsModal}
      {addClientModal}
    </Box>
  );
};

export default ClientsDatagridView;
